import { useEffect, useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { MainLayout } from '../layouts';
import { FeedbackAnalytics, AnalyticsControls, ActiveUsersChart, UsersAnalyticsTable, type RangeMode } from '../components';
import { api, type ExpertMetrics, type AnalyticsRangeOptions } from '../lib/api';

const toDateInputValue = (date: Date) => date.toISOString().slice(0, 10);

// Map preset range to days; custom range is measured from the chosen dates.
// Declared outside the component so it's a stable reference (not a
// useMemo/useCallback dependency that gets recreated every render).
const DAYS_MAP = { today: 1, '7d': 7, '30d': 30, '90d': 90 } as const;

const formatDateTick = (value: string) => {
  const date = new Date(`${value}T00:00:00`);
  if (isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const Analytics = () => {
  const [rangeMode, setRangeMode] = useState<RangeMode>('7d');
  const [customStart, setCustomStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return toDateInputValue(d);
  });
  const [customEnd, setCustomEnd] = useState(() => toDateInputValue(new Date()));
  const [excludeAdminActivity, setExcludeAdminActivity] = useState(false);

  const effectiveDays = useMemo(() => {
    if (rangeMode === 'custom') {
      const start = new Date(`${customStart}T00:00:00`);
      const end = new Date(`${customEnd}T00:00:00`);
      if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return 7;
      return Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000) + 1);
    }
    return DAYS_MAP[rangeMode];
  }, [rangeMode, customStart, customEnd]);

  // The date "as of" which cumulative counts (Total Users) should be
  // snapshotted — the custom range's end date, or today for preset ranges
  const effectiveEndDate = rangeMode === 'custom' && customEnd ? customEnd : toDateInputValue(new Date());

  // Cache key that uniquely identifies the selected range, including custom
  // bounds and the exclude-admin toggle (both affect every query below)
  const rangeKey = `${rangeMode === 'custom' ? `custom-${customStart}-${customEnd}` : rangeMode}-${excludeAdminActivity ? 'ex-admin' : 'all'}`;

  // Shared range + exclude-admin options for the analytics endpoints —
  // prefers the exact custom start/end dates over the day count when set
  const analyticsRangeOptions: AnalyticsRangeOptions = useMemo(
    () =>
      rangeMode === 'custom'
        ? { startDate: customStart, endDate: customEnd, excludeAdmins: excludeAdminActivity }
        : { days: effectiveDays, excludeAdmins: excludeAdminActivity },
    [rangeMode, customStart, customEnd, effectiveDays, excludeAdminActivity]
  );

  // Every query below should always reflect the current state on navigation —
  // this dashboard is a live monitoring view, not something that should show
  // up-to-5-minutes-stale data until the user hard-refreshes the page.
  const alwaysFresh = { staleTime: 0, refetchOnMount: 'always' as const };

  // Fetch conversation analytics from real API
  const { data: conversationAnalytics, isLoading: conversationLoading } = useQuery({
    queryKey: ['conversationAnalytics', rangeKey],
    queryFn: () => api.getConversationAnalytics(analyticsRangeOptions),
    ...alwaysFresh,
  });

  // Fetch system overview from real API — scoped to a cumulative "as of"
  // snapshot so a custom range ending in the past excludes newer users, and
  // to the exclude-admin toggle so all four tiles reflect it, not just Total
  const { data: overviewData, isLoading: overviewLoading } = useQuery({
    queryKey: ['system-overview', effectiveEndDate, excludeAdminActivity],
    queryFn: () => api.getSystemOverview({ asOfDate: effectiveEndDate, excludeAdmins: excludeAdminActivity }),
    ...alwaysFresh,
  });

  // Fetch expert metrics
  const { data: expertMetrics, isLoading: expertMetricsLoading } = useQuery<ExpertMetrics>({
    queryKey: ['expertMetrics', rangeKey],
    queryFn: () => api.getExpertMetrics(),
    refetchInterval: 60000, // Refresh every minute
    ...alwaysFresh,
  });

  // Fetch contact reason analytics
  const { data: contactReasonData, isLoading: contactReasonLoading } = useQuery({
    queryKey: ['contactReasons', rangeKey],
    queryFn: () => api.getContactReasonAnalytics(analyticsRangeOptions),
    ...alwaysFresh,
  });

  // Fetch feedback analytics
  const { data: feedbackAnalytics, isLoading: feedbackLoading, isFetching: feedbackFetching } = useQuery({
    queryKey: ['feedbackAnalytics', rangeKey],
    queryFn: () => api.getFeedbackAnalytics(analyticsRangeOptions),
    ...alwaysFresh,
  });

  // Derive user stats from overview data — the backend already applies the
  // exclude-admin filter and the "as of" cutoff to every one of these counts
  const userStats = overviewData?.data?.users ? {
    total: overviewData.data.users.total,
    approved: overviewData.data.users.active,
    pending: overviewData.data.users.pending,
    disabled: overviewData.data.users.disabled
  } : undefined;

  // Real unique-active-users-per-day series from the conversation analytics trends
  const dailyActivity = useMemo(
    () => (conversationAnalytics?.data?.trends.activeUsersByDay || []).map((point) => ({
      date: point.date,
      activeUsers: point.count,
    })),
    [conversationAnalytics]
  );

  // Derive analytics data from real API
  const analyticsData = conversationAnalytics?.data ? {
    totalConversations: conversationAnalytics.data.summary.totalConversations,
    activeConversations: conversationAnalytics.data.summary.activeConversations,
    totalMessages: conversationAnalytics.data.summary.totalMessages,
    averageResponseTime: conversationAnalytics.data.summary.averageResponseTime,
    topChannels: conversationAnalytics.data.trends.messagesByType.map(t => ({
      channel: t.type,
      count: t.count
    })),
    messagesByDay: conversationAnalytics.data.trends.messagesByDay,
    conversationsByDay: conversationAnalytics.data.trends.conversationsByDay
  } : undefined;

  // Both series are real, straight from the conversation analytics trends —
  // merge messages-by-day and conversations-by-day onto a shared date axis
  const conversationMessageTrend = useMemo(() => {
    const trends = conversationAnalytics?.data?.trends;
    const conversationsByDate = new Map((trends?.conversationsByDay || []).map(c => [c.date, c.count]));
    return (trends?.messagesByDay || []).map((point) => ({
      date: point.date,
      messages: point.count,
      conversations: conversationsByDate.get(point.date) ?? 0,
    }));
  }, [conversationAnalytics]);

  const [showMessagesSeries, setShowMessagesSeries] = useState(true);
  const [showConversationsSeries, setShowConversationsSeries] = useState(false);

  // Users table: search/sort/pagination are all server-driven (see
  // api.getUserAnalytics) so the full user list never has to load client-side
  const [usersSearchInput, setUsersSearchInput] = useState('');
  const [usersSearch, setUsersSearch] = useState('');
  const [usersSortDir, setUsersSortDir] = useState<'asc' | 'desc'>('asc');
  const [usersPage, setUsersPage] = useState(1);
  const [usersPageSize, setUsersPageSize] = useState(10);

  // Debounce the search box so we don't fire a request per keystroke
  useEffect(() => {
    const handle = setTimeout(() => setUsersSearch(usersSearchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [usersSearchInput]);

  // Jump back to page 1 whenever the search, sort, or exclude-admin filter changes
  useEffect(() => {
    setUsersPage(1);
  }, [usersSearch, usersSortDir, excludeAdminActivity]);

  const { data: userAnalyticsData, isLoading: userAnalyticsLoading } = useQuery({
    queryKey: ['analytics-users-table', usersPage, usersPageSize, usersSearch, usersSortDir, excludeAdminActivity, effectiveEndDate],
    queryFn: () => api.getUserAnalytics({
      page: usersPage,
      limit: usersPageSize,
      search: usersSearch || undefined,
      sortDir: usersSortDir,
      excludeAdmins: excludeAdminActivity,
      asOfDate: effectiveEndDate,
    }),
    placeholderData: keepPreviousData,
    ...alwaysFresh,
  });

  const isLoading = conversationLoading || overviewLoading || expertMetricsLoading || contactReasonLoading || feedbackLoading || feedbackFetching;

  const StatCard = ({
    title,
    value,
    icon,
    color = 'blue'
  }: {
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color?: 'blue' | 'green' | 'yellow' | 'red' | 'purple';
  }) => {
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600',
      green: 'bg-green-50 text-green-600',
      yellow: 'bg-yellow-50 text-yellow-600',
      red: 'bg-red-50 text-red-600',
      purple: 'bg-purple-50 text-purple-600'
    };

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            {/* Fixed height reserves room for a 2-line title so the value below
                lines up across cards regardless of how long each title is */}
            <p className="text-sm font-medium text-gray-600 min-h-[2.5rem] flex items-start leading-tight">{title}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            {icon}
          </div>
        </div>
      </div>
    );
  };

  return (
    <MainLayout>
      <div>
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <div className="flex items-center mb-2">
              <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
              <span className="ml-3 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.5-5.5L21 3m0 0h-5.25M21 3v5.25" />
                </svg>
                Super Admin Only
              </span>
            </div>
            <p className="text-gray-600">Monitor system performance and user engagement</p>
          </div>
          <AnalyticsControls
            rangeMode={rangeMode}
            onRangeModeChange={setRangeMode}
            customStart={customStart}
            customEnd={customEnd}
            onCustomStartChange={setCustomStart}
            onCustomEndChange={setCustomEnd}
            excludeAdminActivity={excludeAdminActivity}
            onExcludeAdminActivityChange={setExcludeAdminActivity}
          />
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-gray-600">Loading analytics...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* User Statistics */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">User Statistics</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                  title="Total Users"
                  value={userStats?.total || 0}
                  color="blue"
                  icon={
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-.5a.5.5 0 11-1 0 .5.5 0 011 0z" />
                    </svg>
                  }
                />
                <StatCard
                  title="Approved Users"
                  value={userStats?.approved || 0}
                  color="green"
                  icon={
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  }
                />
                <StatCard
                  title="Pending Approval"
                  value={userStats?.pending || 0}
                  color="yellow"
                  icon={
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  }
                />
                <StatCard
                  title="Disabled Users"
                  value={userStats?.disabled || 0}
                  color="red"
                  icon={
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                    </svg>
                  }
                />
              </div>
            </div>

            {/* Active Users Trend */}
            <div>
              <ActiveUsersChart data={dailyActivity} />
            </div>

            {/* Users Table */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Users</h2>
              <UsersAnalyticsTable
                rows={userAnalyticsData?.data.users || []}
                isLoading={userAnalyticsLoading}
                search={usersSearchInput}
                onSearchChange={setUsersSearchInput}
                sortDir={usersSortDir}
                onSortDirChange={setUsersSortDir}
                page={usersPage}
                pageSize={usersPageSize}
                totalPages={userAnalyticsData?.data.pagination.totalPages || 1}
                onPageChange={setUsersPage}
                onPageSizeChange={(size) => {
                  setUsersPageSize(size);
                  setUsersPage(1);
                }}
              />
            </div>

            {/* Conversation Analytics */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Conversation Analytics</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <StatCard
                  title="Total Conversations"
                  value={analyticsData?.totalConversations || 0}
                  color="purple"
                  icon={
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  }
                />
                <StatCard
                  title="Total Messages"
                  value={analyticsData?.totalMessages || 0}
                  color="blue"
                  icon={
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                  }
                />
              </div>

              {/* Conversation & Message Trend */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Conversations & Messages per Day</h3>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={showConversationsSeries}
                        onChange={(e) => setShowConversationsSeries(e.target.checked)}
                        className="w-3.5 h-3.5 rounded border-gray-300"
                        style={{ accentColor: '#eb6834' }}
                      />
                      Conversations
                    </label>
                    <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={showMessagesSeries}
                        onChange={(e) => setShowMessagesSeries(e.target.checked)}
                        className="w-3.5 h-3.5 rounded border-gray-300"
                        style={{ accentColor: '#2a78d6' }}
                      />
                      Messages
                    </label>
                  </div>
                </div>
                {conversationMessageTrend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={conversationMessageTrend} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e1e0d9" />
                      <XAxis dataKey="date" tickFormatter={formatDateTick} stroke="#898781" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} stroke="#898781" tick={{ fontSize: 12 }} />
                      <Tooltip labelFormatter={(value) => formatDateTick(String(value))} />
                      {(showMessagesSeries || showConversationsSeries) && <Legend />}
                      {showConversationsSeries && (
                        <Line
                          type="monotone"
                          dataKey="conversations"
                          name="Conversations"
                          stroke="#eb6834"
                          strokeWidth={2}
                          dot={false}
                        />
                      )}
                      {showMessagesSeries && (
                        <Line type="monotone" dataKey="messages" name="Messages" stroke="#2a78d6" strokeWidth={2} dot={false} />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-72 flex items-center justify-center text-gray-500 text-sm">No data for the selected range</div>
                )}
              </div>
            </div>

            {/* Expert Analytics */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Expert Performance</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <StatCard
                  title="Total Experts"
                  value={expertMetrics?.expertPerformance?.length || 0}
                  color="purple"
                  icon={
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  }
                />
                <StatCard
                  title="Total Resolved"
                  value={expertMetrics?.expertPerformance?.reduce((acc, p) => acc + p.totalResolved, 0) || 0}
                  color="green"
                  icon={
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  }
                />
                <StatCard
                  title="Avg Response Time"
                  value={`${expertMetrics?.averageResponseTimeMinutes || 0}min`}
                  color="blue"
                  icon={
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  }
                />
                <StatCard
                  title="Pending Escalations"
                  value={expertMetrics?.pendingCount || 0}
                  color="yellow"
                  icon={
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  }
                />
              </div>

              {/* Expert Performance Table */}
              {expertMetrics?.expertPerformance && expertMetrics.expertPerformance.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Expert Leaderboard</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expert</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resolved</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Response</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {expertMetrics.expertPerformance
                          .sort((a, b) => b.totalResolved - a.totalResolved)
                          .slice(0, 10)
                          .map((expert, index) => (
                            <tr key={expert.expertId} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                                    index === 0 ? 'bg-yellow-500' :
                                    index === 1 ? 'bg-gray-400' :
                                    index === 2 ? 'bg-amber-600' : 'bg-blue-500'
                                  }`}>
                                    {index + 1}
                                  </div>
                                  <div className="ml-3">
                                    <div className="text-sm font-medium text-gray-900">{expert.expertName || 'Unknown Expert'}</div>
                                    <div className="text-sm text-gray-500">ID: {expert.expertId.slice(0, 8)}...</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                  {expert.totalResolved} resolved
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                {expert.averageResponseTime}min
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Contact Reason Analytics */}
            {contactReasonData?.data && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Expert Contact Reasons</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                  <StatCard
                    title="Total Escalations"
                    value={contactReasonData.data.summary.totalEscalations}
                    color="purple"
                    icon={
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    }
                  />
                  <StatCard
                    title="With Reason"
                    value={contactReasonData.data.summary.totalWithReasons}
                    color="green"
                    icon={
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    }
                  />
                  <StatCard
                    title="Without Reason"
                    value={contactReasonData.data.summary.totalWithoutReasons}
                    color="yellow"
                    icon={
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    }
                  />
                  <StatCard
                    title="Capture Rate"
                    value={`${contactReasonData.data.summary.reasonCaptureRate}%`}
                    color="blue"
                    icon={
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    }
                  />
                </div>

                {/* Reason Distribution */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Reason Distribution</h3>
                    <p className="text-sm text-gray-600 mt-1">Why users are contacting experts</p>
                  </div>
                  <div className="p-6">
                    <div className="space-y-4">
                      {contactReasonData.data.distribution
                        .sort((a, b) => b.count - a.count)
                        .map((item, index) => {
                          const colors = [
                            'bg-blue-500',
                            'bg-green-500',
                            'bg-yellow-500',
                            'bg-purple-500',
                            'bg-red-500',
                            'bg-indigo-500'
                          ];
                          return (
                            <div key={item.reason}>
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center">
                                  <div className={`w-3 h-3 rounded-full mr-3 ${colors[index % colors.length]}`}></div>
                                  <span className="font-medium text-gray-900">{item.label}</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                  <span className="text-sm text-gray-600">{item.count} requests</span>
                                  <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                                    {item.percentage}%
                                  </span>
                                </div>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${colors[index % colors.length]}`}
                                  style={{ width: `${item.percentage}%` }}
                                ></div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Feedback Analytics */}
            {feedbackAnalytics?.data && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">AI Response Feedback</h2>
                <p className="text-gray-600 mb-6">User feedback on AI-generated responses</p>
                <FeedbackAnalytics data={feedbackAnalytics.data} />
              </div>
            )}

            {/* Channel Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Channel Distribution</h3>
                <div className="space-y-4">
                  {analyticsData?.topChannels.map((channel, index) => (
                    <div key={channel.channel} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full mr-3 ${
                          index === 0 ? 'bg-blue-500' :
                          index === 1 ? 'bg-green-500' : 'bg-purple-500'
                        }`}></div>
                        <span className="font-medium text-gray-900 capitalize">{channel.channel}</span>
                      </div>
                      <span className="text-gray-600">{channel.count} messages</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <button className="w-full flex items-center justify-between p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <span>Export Analytics Report</span>
                    </div>
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  <button className="w-full flex items-center justify-between p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>System Health Check</span>
                    </div>
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  <button className="w-full flex items-center justify-between p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-purple-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span>Configure Alerts</span>
                    </div>
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div> */}
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};
